import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { api, API } from "../lib/api";
import { Reveal, useSeo, cardUrl } from "../lib/ui";
import { StageBar, StageClose } from "../components/StageChain";
import { PriorityEighteen } from "../components/PriorityEighteen";
import { CountyMap } from "../components/CountyMap";
import { CountyLeaderboard } from "../components/CountyLeaderboard";
import { FiledSites } from "../components/FiledSites";
import { CountyCompare } from "../components/CountyCompare";
import { LivePopulation } from "../components/LivePopulation";

export default function Counties() {
  const [data, setData] = useState({ counties: [], facilities: [], national: {} });
  const [pins, setPins] = useState({});
  useSeo({
    title: "County Map — KickStartKenyaFootball",
    description:
      "An interactive Kenya county map: 2019 census population and women-to-men ratio, schooling, GDP PPP estimates, live infrastructure projects and where football development is proposed county by county.",
    image: cardUrl("counties"),
  });

  useEffect(() => {
    api
      .get("/counties")
      .then((r) => setData(r.data))
      .catch(() => setData({ counties: [], facilities: [], national: {} }));
    api
      .get("/apply/map")
      .then((r) => setPins(r.data.counts || {}))
      .catch(() => setPins({}));
  }, []);

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="counties-page">
      <StageBar stage="build" />
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">Stage two — building the game</p>
          <h1 className="display-1 mt-5 max-w-4xl">County Map</h1>
          <p className="body-copy mt-6 max-w-3xl">
            Two thirds of Kenya&rsquo;s league clubs sit in Nairobi while the overwhelming majority of Kenyans live
            elsewhere. That is not a complaint, it is the opportunity: 47 counties, a median age of 20 and a population
            growing by more than a million people a year. This map puts the 2019 census next to the football plan and
            keeps it current with the live national estimate — population today, the women-to-men ratio, schooling,
            county GDP at purchasing power parity, and the jobs, teams, coaching places and health savings each county
            would gain. The eighteen counties carrying the most people and the most output — the Priority 18 — are
            highlighted as their own tier, because that is where the next decade of growth has to start.
          </p>
          <div className="mono mt-8 flex flex-wrap gap-8 border-t border-white/10 pt-6 text-sm">
            <span data-testid="counties-count">
              <span className="text-ochre">{data.counties.length}</span> counties mapped
            </span>
            <span data-testid="counties-population">
              <span className="text-ochre">
                {data.national?.population ? (data.national.population / 1e6).toFixed(1) : "—"}m
              </span>{" "}
              people counted in 2019
            </span>
            <span data-testid="counties-population-live">
              <span className="text-ochre">
                {data.population?.live_population ? (data.population.live_population / 1e6).toFixed(2) : "—"}m
              </span>{" "}
              alive today (Worldometer)
            </span>
            <Link to="/money" className="text-zinc-400 underline decoration-maasai underline-offset-4 hover:text-white">
              The money behind it
            </Link>
            <Link to="/apply" className="text-ochre underline decoration-maasai underline-offset-4 hover:text-white" data-testid="counties-apply-link">
              Tell us about an opportunity in your county
            </Link>
          </div>
        </Reveal>

        <div className="mt-14">
          <LivePopulation pop={data.population} />
        </div>

        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/apply" className="btn-primary" data-testid="counties-apply-cta">
            Make a proposal for your county
          </Link>
          <Link to="/facilities" className="btn-ghost" data-testid="counties-facilities-link">
            Facility costs and cost per pro
          </Link>
          <Link to="/court-rollout" className="btn-ghost" data-testid="counties-rollout-link">
            Courts before AFCON
          </Link>
          <Link to="/sources" className="btn-ghost" data-testid="counties-sources-link">
            Sources and Method
          </Link>
          <a href={`${API}/research-pack.pdf`} className="btn-primary" data-testid="counties-prospectus-download">
            <Download size={14} /> Download the research pack
          </a>
        </div>

        <div className="mt-14">
          <CountyMap
            counties={data.counties}
            censusSource={data.census_source}
            censusSourceUrl={data.census_source_url}
            pop={data.population}
            pins={pins}
          />
        </div>

        <FiledSites limit={12} />

        <PriorityEighteen />

        <CountyLeaderboard limit={12} />

        <div className="mt-20" data-testid="counties-compare-section">
          <p className="eyebrow">Head to head</p>
          <h2 className="display-2 mt-4">Compare two counties</h2>
          <p className="body-copy mt-5 max-w-3xl">
            Pick any two counties and see who has the people, the schools, the youth and the pitches. It is the fastest
            way to see how unevenly Kenyan football investment is spread.
          </p>
          <div className="mt-10">
            <CountyCompare counties={data.counties} />
          </div>
        </div>

        <div className="mt-20" data-testid="counties-steps-section">
          <p className="eyebrow">Recommended steps</p>
          <h2 className="display-2 mt-4">What the plan looks like in one county</h2>
          <p className="body-copy mt-5 max-w-3xl">
            Every county has its own version of the recommended steps, priced from its own population and its own share of
            the allocation. Two tracks: standing up a licensed professional club, and organising the community game
            around it.
          </p>
          <div className="mt-8 grid gap-px border border-zinc-800 bg-zinc-800 md:grid-cols-2">
            {[
              ["The Professional Game", "professional", "Incorporation, governance and land, then a business plan and trademark, consents, a club base, squad and coach licensing, FKF club licensing and a modular stadium."],
              ["The National Game", "national", "A county football plan, constituted and safeguarded clubs, courts and pitch upgrades, leagues, coaches and referees, schools and girls, and a route into the professional club."],
            ].map(([label, key, note]) => (
              <Link
                key={key}
                to={`/plans?track=${key}`}
                data-testid={`counties-steps-${key}`}
                className="group bg-[#0b0b0b] p-8 transition-colors hover:bg-[#151515] md:p-10"
              >
                <p className="overline text-ochre">Eight steps</p>
                <h3 className="mt-4 font-display text-xl uppercase tracking-tight text-white transition-colors group-hover:text-ochre md:text-2xl">
                  {label}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-zinc-400">{note}</p>
                <span className="mono mt-6 block text-[0.6rem] uppercase tracking-[0.18em] text-zinc-500 group-hover:text-ochre">
                  Open the steps →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
      <StageClose stage="build" />
    </div>
  );
}
